import {
  Box,
  Card,
  Dialog,
  DialogContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  styled
} from '@mui/material';
import { FormattedNumber, IntlProvider } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { Small } from '../../../components/Typography';
import '../../credio/user/user.css';
import { useParams } from 'react-router-dom';
import { MatxLoading, SimpleCard } from '../../../components';
import useCredio from '../../../hooks/useCredio';
import Receipt from '../../credio/user/receipt';
import './styles/transactions.css';
import ReceiptReader from '../../../views/reader-receipt/receiptReader';
import { getSuccessful } from '../../../utils/utils';

const Heading = styled('h6')(({ theme }) => ({
  margin: 0,
  marginTop: '4px',
  fontSize: '14px',
  fontWeight: '500',
  color: theme.palette.primary.main,
}));

const ContentBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  '& small': { color: theme.palette.text.secondary },
  '& .icon': { opacity: 0.6, fontSize: '44px', color: theme.palette.primary.main },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px !important',
  background: theme.palette.background.paper,
  [theme.breakpoints.down('sm')]: { padding: '16px !important' },
}));

const Container = styled('div')(({ theme }) => ({
  margin: '30px',
  [theme.breakpoints.down('sm')]: { margin: '16px' },
  '& .breadcrumb': {
    marginBottom: '30px',
    [theme.breakpoints.down('sm')]: { marginBottom: '16px' },
  },
}));

const TableTransactionDetails = ({ type }) => {
  const {
    loadedTranxOver,
    merchant,
    credio,
    transactionsVasSavingsOverView,
    loadedReaderTran,
    loadedTranxVasSavings,
    transactionsOverView,
    getTransactionsVasSavingsOver,
    getReaderTransactions,
    getTransactionsOver,
    date
  } = useCredio();

  const [data, setData] = useState({});
  const [openDialogReceipt, setOpenDialogReceipt] = useState(false);
  const [fullWidth] = useState(true);
  const [maxWidth] = useState('sm');
  const [searching, setSearching] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [cardList, setCardList] = useState([]);
  const [header, setHeader] = useState('');

  useEffect(() => {
    console.log("type ....");
    switch (type) {
      case 'deposit': getTransactionsOver(0, date); break;
      case 'reversals': getTransactionsOver(2, date); break;
      case 'withdrawals': getTransactionsOver(1, date); break;
      case 'reader': getReaderTransactions(date); break;
      case 'coralpay': getTransactionsVasSavingsOver(2, date); break;
      case 'shm': getTransactionsVasSavingsOver(0, date); break;
      case 'card': getTransactionsOver(1, date); break;
      case 'saving': getTransactionsVasSavingsOver(1, date); break;
      default: break;
    }
  }, [type]);

  const openDialogReceipts = (d) => {
    setData(d);
    setOpenDialogReceipt(true);
  };

  const handleCloseDialogReceipt = () => {
    setOpenDialogReceipt(false);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleTextSearch = (e) => {
    setSearching(e.target.value.toLowerCase());
  };

  const filteredTransactions = () => {
    let data;
    switch (type) {
      case 'deposit':
      case 'withdrawals':
      case 'reversals':
      case 'card':
        data = transactionsOverView;
        break;
      case 'reader':
        data = merchant;
        break;
      case 'coralpay':
      case 'shm':
      case 'saving':
        data = transactionsVasSavingsOverView;
        break;
      default:
        break;
    }

    return data?.data?.transaction?.filter((e) =>
      !searching
        ? true
        : e.from?.toLowerCase().includes(searching) || e.phoneNumber?.toLowerCase().includes(searching)
    ) || [];
  };

  const loadingFunction = () => {
    switch (type) {
      case 'deposit':
      case 'withdrawals':
        return loadedTranxOver;
      case 'reader':
        return loadedReaderTran;
      case 'coralpay':
      case 'shm':
      case 'saving':
        return loadedTranxVasSavings;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (loadedTranxOver && transactionsOverView?.data) {
      const { transactionLength, totalFee, fees, transaction } = transactionsOverView.data;
      const totalAmount = transaction.reduce((sum, t) => sum + (t.amount || 0), 0);
      setHeader(type);
      if (type !== 'deposit') {
        setCardList([
          { name: 'Total Transaction', isAmount: true, amount: totalAmount, icon: 'group' },
          { name: 'Total Transaction Volume', amount: `${transactionLength}`, isAmount: false, icon: 'attach_money' },
          { name: 'Total Fee', isAmount: true, amount: totalFee, icon: 'edit_note' },
          { name: 'Profit Fee', isAmount: true, amount: fees, icon: 'credit_card' }
        ]);
      }
    }
  }, [loadedTranxOver, transactionsOverView, type]);

  const getStatusTag = (subscriber) => {
    const status = subscriber?.referenceData?.status;
    const vendStatus = subscriber?.referenceData?.vendStatus;

    if (subscriber.mode === 1 || subscriber.mode === 2 || subscriber.mode === 8 ) {
      return getSuccessful(subscriber.referenceData) ? 'tag green' : 'tag red';
    } else if (subscriber.mode === 4) {
      return status === 'successful' || vendStatus === 'CONFIRMED' ? 'tag green' : 'tag red';
    } else {
      return status === 'Created' ? 'tag red' : 'tag green';
    }
  };

  // Move the loading check to render time, not before hooks
  const isLoading = !loadingFunction();

  // Show loading state without early return
  if (isLoading) {
    return <MatxLoading />;
  }

  const canUseNarration = () => ['coralpay', 'shm', 'card', 'reader'].includes(type);
  const canUseReaderReceipt = () => ['card', 'reader'].includes(type);

  return (
    <Container>
      {type === 'withdrawals' && (
        <IntlProvider locale="en" defaultLocale="en">
          <Grid container spacing={3} sx={{ mb: '24px' }}>
            {cardList.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <StyledCard elevation={6}>
                  <ContentBox>
                    <Box ml="12px">
                      <Small>{item.name}</Small>
                      <Heading>
                        {item.isAmount ? (
                          <FormattedNumber value={item.amount} style="currency" currency="NGN" />
                        ) : (
                          item.amount
                        )}
                      </Heading>
                    </Box>
                  </ContentBox>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </IntlProvider>
      )}

      <Dialog open={openDialogReceipt} fullWidth={fullWidth} maxWidth={maxWidth} onClose={handleCloseDialogReceipt}>
        <DialogContent className="removePadding">
          {!canUseReaderReceipt()
            ? <Receipt data={data} users={[]} banks={credio?.data?.banks} onCLick={handleCloseDialogReceipt} />
            : <ReceiptReader data={data} />}
        </DialogContent>
      </Dialog>

      <SimpleCard
        showIconWidget={true}
        showIcon={false}
        iconWidget={
          <TextField
            type="search"
            id="search"
            placeholder="Search"
            onChange={handleTextSearch}
            sx={{ width: 400 }}
          />
        }
      >
        <Box width="100%" overflow="auto">
          <Table>
            <TableHead>
              <TableRow>
                {!canUseNarration() && <TableCell>From</TableCell>}
                {!canUseNarration() && <TableCell>To</TableCell>}
                {canUseNarration() && <TableCell size="medium">Narration</TableCell>}
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions()
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((subscriber, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => subscriber.mode !== 4 && openDialogReceipts(subscriber)}
                    style={{ cursor: 'pointer' }}
                  >
                    {!canUseNarration() && <TableCell>{subscriber.from}</TableCell>}
                    {!canUseNarration() && <TableCell>{subscriber.to}</TableCell>}
                    {canUseNarration() && <TableCell>{subscriber.narration}</TableCell>}
                    <TableCell>₦ {subscriber.amount}</TableCell>
                    <TableCell>
                      <span className={getStatusTag(subscriber)}>
                        {subscriber.referenceData?.status || subscriber.referenceData?.vendStatus || subscriber.referenceData?.messageReason || 'Unknown'}
                      </span>
                    </TableCell>
                    <TableCell>{subscriber.createdAt || subscriber.date}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            component="div"
            rowsPerPage={rowsPerPage}
            count={filteredTransactions().length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </SimpleCard>
    </Container>
  );
};

export default TableTransactionDetails;